import { useEffect, useRef, useState } from 'react'
import { Button, IconButton, Select, Text, TextField, SidePanel } from '@nike/eds'
import type { MultiValue } from '@nike/eds'
import { Close } from '@nike/nike-design-system-icons'
import { Column, ColumnFiltersState } from '@tanstack/react-table'
import { MonthYearPicker } from './MonthYearPicker'

export type SidePanelColumn<T = any> = Column<T, any> & {
  columnDef: {
    header: string | React.ReactNode | (() => React.ReactNode)
    filterLabel?: string
    filterType?: 'text' | 'select' | 'multiselect' | 'dateRange' | 'date' | 'monthRange' | 'month'
    filterOptions?: Array<{ value: string; label: string }>
    getFilterOptions?: (tableState: any) => Array<{ value: string; label: string }>
    filterGroup?: string
  }
}

type ColumnFiltersSidePanelProps = {
    isOpen: boolean
    onClose: () => void
    table: any
    columns: {
        id: string
        getCanFilter: () => boolean
        getFilterValue: () => string | string[] | object | undefined
        setFilterValue: (value: string | string[] | object | undefined) => void
        columnDef: {
            header: string | React.ReactNode | (() => React.ReactNode);
            filterLabel?: string;
            filterType?: 'text' | 'select' | 'multiselect' | 'dateRange' | 'date' | 'monthRange' | 'month'
            filterOptions?: Array<{ value: string; label: string }>
            getFilterOptions?: (tableState: any) => Array<{ value: string; label: string }>
            filterKey?: string;
            filterGroup?: string;
        }
    }[]
    columnFilters: ColumnFiltersState
    setColumnFilters?: React.Dispatch<React.SetStateAction<ColumnFiltersState>>
    handleColumnFiltersChange?: (filters: ColumnFiltersState) => void
    globalFilter?: string
    onGlobalFilterChange?: (value: string) => void
    globalFilterPlaceholder?: string
}

export function ColumnFiltersSidePanel(
    {
        isOpen,
        onClose,
        table,
        columns,
        columnFilters,
        handleColumnFiltersChange,
        globalFilter,
        onGlobalFilterChange,
        globalFilterPlaceholder = 'Search...',
    }: ColumnFiltersSidePanelProps) {

    const panelRef = useRef<HTMLDivElement>(null)
    const [localGlobalFilter, setLocalGlobalFilter] = useState(globalFilter || '')

    useEffect(() => {
        setLocalGlobalFilter(globalFilter || '')
    }, [globalFilter])

    useEffect(() => {
        if (!isOpen) return

        const handleClickOutside = (event: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen, onClose])

    const handleClearFilters = () => {
        if (handleColumnFiltersChange) {
            handleColumnFiltersChange([])
        }
        if (onGlobalFilterChange) {
            setLocalGlobalFilter('')
            onGlobalFilterChange('')
        }
    }
    
    const handleFilterChange = (columnId: string, value: string | string[] | object | undefined) => {
        const newFilters = columns
            .map(col => {
                const val = col.id === columnId ? value : col.getFilterValue();
                if (val === undefined || val === '') return null;
                if (Array.isArray(val) && val.length === 0) return null;
                return { id: col.id, value: val };
            })
            .filter(Boolean) as ColumnFiltersState;

        if (handleColumnFiltersChange) {
            handleColumnFiltersChange(newFilters);
        }
    };
  return (
    <div ref={panelRef}>
    <SidePanel
        hideScrim={true}
        isOpen={isOpen}
        onDismiss={onClose}
        headerSlot={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Text font="title-5">Filters</Text>
                <IconButton icon={<Close />} label="Close filters" size="small" variant="ghost" onClick={() => onClose()} style={{ marginRight: '-12px' }} />
            </div>
        }
        footerSlot={
           <Button disabled={columnFilters.length === 0 && !globalFilter} style={{ width: "100%" }} variant="primary" onClick={handleClearFilters}>Clear Filters</Button>
        }
        hideFade={true}>
            
      <div className="eds-spacing--pb--64" style={{'minHeight': '50vh'}}>
        {onGlobalFilterChange && (
            <div className="eds-flex eds-flex--direction-column eds-gap--8 eds-spacing--mb-16">
                <TextField
                    label="Search"
                    id="table-sidepanel-global-filter"
                    type="search"
                    value={localGlobalFilter}
                    onChange={(e) => {
                        setLocalGlobalFilter(e.target.value)
                        onGlobalFilterChange!(e.target.value)
                    }}
                    placeholder={globalFilterPlaceholder}
                />
            </div>
        )}
        {(() => {
            const filterableColumns = columns.filter(col => col.getCanFilter());
            const groupMap = new Map<string | undefined, typeof filterableColumns>();
            const groupOrder: (string | undefined)[] = [];
            for (const column of filterableColumns) {
                const group = column.columnDef.filterGroup;
                if (!groupMap.has(group)) {
                    groupMap.set(group, []);
                    groupOrder.push(group);
                }
                groupMap.get(group)!.push(column);
            }
            const groups = groupOrder.map(label => ({ label, columns: groupMap.get(label)! }));
            return groups.map((group, groupIdx) => (
                <div
                    key={group.label ?? `group-${groupIdx}`}
                    style={group.label ? {
                        background: 'var(--eds-color-background-secondary, #f5f5f5)',
                        border: '1px solid var(--eds-color-border-secondary, #e0e0e0)',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '16px',
                    } : undefined}
                >
                    {group.label && (
                        <Text font="body-3" className="eds-spacing--mb-12" style={{
                            borderBottom: '1px solid var(--eds-color-border-primary, #111)',
                            paddingBottom: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontWeight: 500,
                        }}>{group.label}</Text>
                    )}
                    {group.columns.map((column) => {
                        const filterValue = column.getFilterValue() as string | { from?: string; to?: string }
                        const filterType = column.columnDef.filterType || 'text'
                        const label =
                            column.columnDef.filterLabel ||
                            (typeof column.columnDef.header === 'string' ? column.columnDef.header : '');

                        let filterOptions =
                            column.columnDef.getFilterOptions?.(table) ||
                            column.columnDef.filterOptions ||
                            [];
                        if (
                            filterType === 'select' &&
                            (!filterOptions.length || filterOptions[0].value !== '')
                        ) {
                            filterOptions = [{ value: '', label: 'All' }, ...filterOptions];
                        }
                        return (
                            <div key={column.id} className="eds-flex eds-flex--direction-column eds-gap--8 eds-spacing--mb-16">
                                {filterType === 'text' && (
                                    <TextField 
                                        label={label}
                                        id={`table-sidepanel-filter-${column.id}`}
                                        type="search"
                                        value={typeof filterValue === 'string' ? filterValue : ''}
                                        onChange={(e) => handleFilterChange(column.id, e.target.value || undefined)}
                                        placeholder="Type to filter..."/>)}
                                {filterType === 'date' && (
                                    <TextField
                                        label={label}
                                        id={`table-sidepanel-filter-${column.id}`}
                                        type="date"
                                        value={typeof filterValue === 'string' ? filterValue : ''}
                                        onChange={(e) => handleFilterChange(column.id, e.target.value || undefined)}
                                    />
                                )}
                                {filterType === 'month' && (
                                    <MonthYearPicker
                                        id={`table-sidepanel-filter-month-${column.id}`}
                                        label={label}
                                        value={typeof filterValue === 'string' ? filterValue : ''}
                                        onChange={(val) => handleFilterChange(column.id, val)}
                                    />
                                )}
                                {filterType === 'select' && (
                                    <Select
                                        id={`table-sidepanel-filter-select-${column.id}`}
                                        label={label}
                                        menuPosition='absolute'
                                        message=''
                                        options={filterOptions}
                                        value={
                                            filterOptions.find((option) => option.value === filterValue) || {
                                                value: '',
                                                label: 'All',
                                            }
                                        }
                                        onChange={(e) => handleFilterChange(column.id, e?.value || undefined)}
                                    />)}
                                {filterType === 'multiselect' && (() => {
                                    const selectedValues = Array.isArray(filterValue) ? filterValue as string[] : [];
                                    const selectedOptions = filterOptions.filter(o => selectedValues.includes(o.value));
                                    return (
                                        <Select
                                            id={`table-sidepanel-filter-multiselect-${column.id}`}
                                            label={label}
                                            isMulti
                                            menuPosition='absolute'
                                            message=''
                                            options={filterOptions}
                                            value={selectedOptions}
                                            onChange={(selected: MultiValue<{ value: string; label: string }>) => {
                                                const values = selected ? selected.map(o => o.value) : [];
                                                handleFilterChange(column.id, values.length > 0 ? values : undefined);
                                            }}
                                        />
                                    );
                                })()}
                                {filterType === 'dateRange' && (
                                    <>
                                    <Text font={'title-5'}>{label}</Text>
                                    <div className="eds-flex eds-gap--8">
                                        <TextField
                                            id={`table-sidepanel-filter-date-from-${column.id}`}
                                            label="From"
                                            type="date"
                                            value={typeof filterValue === 'object' && filterValue?.from ? filterValue.from : ''}
                                            onChange={(e) => handleFilterChange(column.id, { ...(typeof filterValue === 'object' && filterValue ? filterValue : {}), from: e.target.value || undefined })}
                                        />
                                        <TextField
                                            id={`table-sidepanel-filter-date-to-${column.id}`}
                                            label="To"
                                            type="date"
                                            value={typeof filterValue === 'object' && filterValue?.to ? filterValue.to : ''}
                                            onChange={(e) => handleFilterChange(column.id, { ...(typeof filterValue === 'object' && filterValue ? filterValue : {}), to: e.target.value || undefined })}
                                        />
                                    </div>
                                    </>
                                )}
                                {filterType === 'monthRange' && (
                                    <>
                                    <Text font={'title-5'}>{label}</Text>
                                    <div className="eds-flex eds-gap--8">
                                        <MonthYearPicker
                                            id={`table-sidepanel-filter-month-from-${column.id}`}
                                            label="From"
                                            value={typeof filterValue === 'object' && filterValue?.from ? filterValue.from : ''}
                                            onChange={(val) => handleFilterChange(column.id, { ...(typeof filterValue === 'object' && filterValue ? filterValue : {}), from: val })}
                                        />
                                        <MonthYearPicker
                                            id={`table-sidepanel-filter-month-to-${column.id}`}
                                            label="To"
                                            value={typeof filterValue === 'object' && filterValue?.to ? filterValue.to : ''}
                                            onChange={(val) => handleFilterChange(column.id, { ...(typeof filterValue === 'object' && filterValue ? filterValue : {}), to: val })}
                                        />
                                    </div>
                                    </>
                                )}
                            </div>
                        )
                    })}
                </div>
            ))
        })()}
        </div>
    </SidePanel>
    </div>
  )
}
